<template>
  <div>
    <b-sidebar
      id="sidebar-franchise-manual"
      sidebar-class="sidebar-lg"
      :visible="newFranchiseManualSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="(val) => mutateNewFranchiseManualSidebar({ visible: val })"
      @hidden="clear"
      @shown="onShow"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h4>{{ newFranchiseManualSidebar.id ? 'Editar' : 'Novo' }} manual do franqueado</h4>
          <div>
            <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
          </div>
        </div>
        <template>
          <b-row class="justify-content-md-center mt-2" v-if="loading">
            <b-spinner big />
          </b-row>
          <b-form v-else @submit.prevent="onSubmit" class="p-1">
            <!-- Name -->
            <b-row>
              <b-col class="mt-1">
                <b-form-group label="Nome" label-for="franchise-manual-name">
                  <b-form-input
                    id="franchise-manual-name"
                    v-model="name"
                    :class="{ 'is-invalid': v$.name.$error }"
                  />
                  <div class="invalid-feedback">
                    <span v-if="v$.name.required.$invalid">Você deve informar o nome do manual</span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Category -->
            <b-row>
              <b-col class="mt-1">
                <b-form-group label="Categoria" label-for="franchise-manual-category">
                  <b-form-input
                    id="franchise-manual-category"
                    v-model="category"
                    :class="{ 'is-invalid': v$.category.$error }"
                  />
                  <div class="invalid-feedback">
                    <span v-if="v$.category.required.$invalid">Você deve informar a categoria</span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Contract types -->
            <b-row>
              <b-col class="mt-1">
                <b-form-group label="Tipos de franquia aplicáveis" label-for="franchise-manual-contract-types">
                  <v-select
                    id="franchise-manual-contract-types"
                    multiple
                    v-model="contractTypeIds"
                    :reduce="(option) => option.id"
                    :options="contractTypeOptions"
                    label="name"
                    close-on-select
                    :class="{ 'is-invalid': v$.contractTypeIds.$error }"
                  >
                    <template #no-options>Sem resultados</template>
                  </v-select>
                  <div class="invalid-feedback">
                    <span v-if="v$.contractTypeIds.required.$invalid">
                      Selecione ao menos um tipo de franquia
                    </span>
                  </div>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Mandatory / Active -->
            <b-row>
              <b-col class="mt-1">
                <b-form-checkbox id="franchise-manual-is-mandatory" v-model="isMandatory" switch inline>
                  Leitura obrigatória
                </b-form-checkbox>
              </b-col>
              <b-col class="mt-1">
                <b-form-checkbox id="franchise-manual-is-active" v-model="isActive" switch inline>
                  Ativo
                </b-form-checkbox>
              </b-col>
            </b-row>

            <!-- First version fields: only on create -->
            <template v-if="!newFranchiseManualSidebar.id">
              <hr class="my-2" />
              <p class="text-muted small mb-1">Primeira versão do manual</p>

              <b-row>
                <b-col class="mt-1">
                  <b-form-group label="Versão" label-for="franchise-manual-version">
                    <b-form-input
                      id="franchise-manual-version"
                      v-model="version"
                      placeholder="Ex: 1.0"
                      :class="{ 'is-invalid': v$.version.$error }"
                    />
                    <div class="invalid-feedback">
                      <span v-if="v$.version.required.$invalid">Você deve informar a versão</span>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col class="mt-1">
                  <file-chooser
                    id="franchise-manual-file"
                    label="Arquivo do manual"
                    onlyPDF
                    v-model="file"
                    :thereIsError="v$.file.$error"
                    errorMessage="Você deve anexar o arquivo do manual (PDF)"
                  />
                </b-col>
              </b-row>
            </template>

            <!-- Form Actions -->
            <b-row>
              <b-col class="mt-1">
                <div class="d-flex mt-2">
                  <b-button :disabled="saving" variant="primary" class="mr-2" type="submit">
                    {{ saving ? "Salvando..." : "Salvar" }}
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </b-form>
        </template>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BFormCheckbox, BButton, BRow, BCol, BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import vSelect from 'vue-select'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import FileChooser from '@/modules/shared/components/FileChooser'
import * as types from '../store/types'

export default {
  components: {
    BSidebar, BForm, BFormGroup, BFormInput, BFormCheckbox, BButton, BRow, BCol, BSpinner,
    vSelect,
    FileChooser,
  },
  data() {
    return {
      name: undefined,
      category: undefined,
      contractTypeIds: [],
      isMandatory: true,
      isActive: true,
      version: undefined,
      file: undefined,
      contractTypeOptions: [],
      loading: false,
      saving: false,
    }
  },
  computed: {
    ...mapGetters({
      newFranchiseManualSidebar: types.NEW_FRANCHISE_MANUAL_SIDEBAR,
    }),
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() }
  },
  validations() {
    return {
      name: { required },
      category: { required },
      contractTypeIds: { required },
      version: { required: requiredIf(() => !this.newFranchiseManualSidebar.id) },
      file: { required: requiredIf(() => !this.newFranchiseManualSidebar.id) },
    }
  },
  methods: {
    ...mapMutations({
      mutateNewFranchiseManualSidebar: types.MUTATE_NEW_FRANCHISE_MANUAL_SIDEBAR,
    }),
    ...mapActions({
      createFranchiseManual: types.CREATE_FRANCHISE_MANUAL,
      updateFranchiseManual: types.UPDATE_FRANCHISE_MANUAL,
      getFranchiseManual: types.GET_FRANCHISE_MANUAL,
      getContractTypeOptions: types.GET_FRANCHISE_CONTRACT_TYPE_OPTIONS,
    }),
    onShow() {
      this.resetFormFields()
      this.loadContractTypeOptions()
      if (this.newFranchiseManualSidebar.id) {
        this.loading = true
        this.getFranchiseManual(this.newFranchiseManualSidebar.id)
          .then((response) => {
            this.name = response.data.name
            this.category = response.data.category
            this.isMandatory = !!response.data.is_mandatory
            this.isActive = !!response.data.is_active
            this.contractTypeIds = (response.data.contract_types || []).map(
              (contractType) => contractType.user_contract_type_id
            )
          })
          .catch(() => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: 'Oops!',
                text: 'Ocorreu um erro ao carregar o manual. Entre em contato com o setor de TI.',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
            this.mutateNewFranchiseManualSidebar({ visible: false })
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate()
      if (!isFormCorrect) return
      this.saving = true

      const savePromise = this.newFranchiseManualSidebar.id
        ? this.updateFranchiseManual({
            id: this.newFranchiseManualSidebar.id,
            name: this.name,
            category: this.category,
            is_mandatory: this.isMandatory,
            is_active: this.isActive,
            contract_type_ids: this.contractTypeIds,
          })
        : this.createFranchiseManual(this.buildCreateFormData())

      savePromise
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso',
              text: 'O manual foi salvo com sucesso!',
              icon: 'CoffeeIcon',
              variant: 'success',
            },
          })
          this.newFranchiseManualSidebar.saveAction()
          this.mutateNewFranchiseManualSidebar({ visible: false })
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao salvar o manual. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          this.saving = false
        })
    },
    buildCreateFormData() {
      const content = {
        name: this.name,
        category: this.category,
        is_mandatory: this.isMandatory ? 1 : 0,
        is_active: this.isActive ? 1 : 0,
        version: this.version,
        file: this.file,
      }
      const request = new FormData()
      for (const [key, value] of Object.entries(content)) {
        if (value !== undefined && value !== null && value !== '') {
          request.append(key, value)
        }
      }
      this.contractTypeIds.forEach((id) => request.append('contract_type_ids[]', id))
      return request
    },
    loadContractTypeOptions() {
      this.getContractTypeOptions()
        .then((response) => {
          this.contractTypeOptions = response.data || []
        })
        .catch(() => {})
    },
    resetFormFields() {
      this.name = undefined
      this.category = undefined
      this.contractTypeIds = []
      this.isMandatory = true
      this.isActive = true
      this.version = undefined
      this.file = undefined
      this.loading = false
      this.saving = false
      this.v$.$reset()
    },
    clear() {
      this.resetFormFields()
      this.mutateNewFranchiseManualSidebar({ id: undefined })
    },
  },
}
</script>
